import authConfig from '../../common/service/authConfig.js'

/*
 * Signing out of a company identity provider, as well as out of Hourglass (#677).
 *
 * Logging out ends two sessions: the app's tokens and Cognito's own. It does **not** end the
 * session at the identity provider - Microsoft keeps a cookie, usually from "Stay signed in?" - so
 * pressing "Sign in with Microsoft" straight afterwards is answered silently and the user is back
 * in. That reads as a login screen that does not log out.
 *
 * Ending it means a redirect to the provider's logout endpoint, and that endpoint cannot be derived
 * from a Cognito token: it is per company, so the server sends it with the rest of the routing.
 *
 * The redirect happens on the way *back* from Cognito's logout, not before it, so both sessions are
 * ended in one gesture:
 *
 *   logout -> Cognito /logout -> back to the app -> provider /logout -> back to the app -> login
 *
 * The flag is what tells the boot on the way back that it owes the provider a redirect. It is
 * cleared *before* redirecting, so returning from the provider is an ordinary page load rather than
 * a loop.
 */
const FLAG = 'hourglass:pendingIdpLogout'

const storage = () => {
  try {
    return window.localStorage
  } catch (err) {
    return null
  }
}

const routing = () => {
  try {
    return authConfig.get().sso || null
  } catch (err) {
    return null
  }
}

/** Arm the redirect for after Cognito's logout. No-op when this company has no provider logout. */
export const armIdpLogout = () => {
  const sso = routing()
  if (!sso || !sso.logoutUrl) return false

  const store = storage()
  if (!store) return false
  store.setItem(FLAG, '1')
  return true
}

export const isIdpLogoutPending = () => {
  const store = storage()
  return Boolean(store && store.getItem(FLAG))
}

export const disarmIdpLogout = () => {
  const store = storage()
  if (store) store.removeItem(FLAG)
}

/** The provider's logout address, coming back to the app. Exported so it can be tested plainly. */
export const idpLogoutUrl = (sso, base) => `${sso.logoutUrl}?post_logout_redirect_uri=${encodeURIComponent(base)}`

/**
 * Called from the boot: if the last thing we did was log out, finish the job at the provider and
 * come back here. Returns true when the browser is leaving.
 */
export const completeIdpLogout = () => {
  if (!isIdpLogoutPending()) return false

  // Before navigating, so coming back does not start over.
  disarmIdpLogout()

  const sso = routing()
  if (!sso || !sso.logoutUrl) return false

  window.location.assign(idpLogoutUrl(sso, `${window.location.origin}/`))
  return true
}

export default { armIdpLogout, isIdpLogoutPending, disarmIdpLogout, completeIdpLogout, idpLogoutUrl }
